import { pipe } from 'fp-ts/function'
import React, { useEffect, useState } from 'react'
import { View, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { AuthService, PolicyDispatchingPermission, SelectOption, SelectSearch } from '@pulseops/common'
import { PolicyDispatchingOfficesResponse, PolicyDispatchingService } from '../services/PolicyDispatchingService'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import { getPermissions } from '../utils/policyDispatchingUtils'

type FormValues = {
  releasedDate: Date | null
  officeCode: SelectOption | null
  policyNumber: string | null
  status: SelectOption | null
  note: string | null
  entryDate: Date | null
  agentCode: string | null
  officeBranchCode: string | null
  officeBankCode: string | null
  systemDate: string | null
}

const formatDate = (date: Date) => {
  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export default function PolicyDispatchingTopSection() {
  const { officeCode, setOfficeCode: publishOfficeCode } = usePolicyDispatchingOffice()
  const { control, setValue } = useForm<FormValues>({
    defaultValues: {
      officeCode: { value: 'QLU', label: 'QLU' },
      officeBranchCode: null,
      officeBankCode: null,
      systemDate: null
    }
  })
  const [offices, setOffices] = useState<PolicyDispatchingOfficesResponse | null>(null)
  const [officesOptions, setOfficesOptions] = useState<SelectOption[]>([])
  const [officeBranchCode, officeBankCode, systemDate] = useWatch({
    control,
    name: ['officeBranchCode', 'officeBankCode', 'systemDate']
  })
  const permissions = getPermissions()
  const allowChangeOffice = permissions.includes(PolicyDispatchingPermission.TransferOffice)
  const today = new Date()

  useEffect(() => {
    pipe(
      ZIO.zipPar(AuthService.userInfo, PolicyDispatchingService.getOffices),
      ZIO.map(([userInfo, res]) => {
        const officeOptions = res.body.items.map((office) => ({
          value: office.officeCode,
          label: office.officeCode
        }))
        setOffices(res)
        setOfficesOptions(officeOptions)
        const selectedOffice =
          officeOptions.find((option) => option.value === userInfo.officeCode) ||
          officeOptions.find((option) => option.value === officeCode) ||
          officeOptions[0] ||
          null
        const selectedOfficeDetails = res.body.items.find((office) => office.officeCode === selectedOffice?.value)
        setValue('officeCode', selectedOffice)
        publishOfficeCode(selectedOffice?.value || null)
        setValue('systemDate', res.body.systemDate)
        setValue('officeBranchCode', selectedOfficeDetails?.officeBranchCode || null)
        setValue('officeBankCode', selectedOfficeDetails?.officeBankCode || null)
      }),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to load policy dispatching offices', error)
        })
      ),
      ZIO.unsafeRun({})
    )
  }, [])

  const handleUpdateOfficeCode = (selectedOfficeCode: string | null) => {
    if (selectedOfficeCode && offices) {
      const selectedOffice = offices.body.items.find((office) => office.officeCode === selectedOfficeCode)
      setValue('officeBranchCode', selectedOffice?.officeBranchCode || null)
      setValue('officeBankCode', selectedOffice?.officeBankCode || null)
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.detailsRow}>
        <View style={styles.officeGroup}>
          <Text style={styles.label}>Văn phòng:</Text>
          <Controller
            control={control}
            name="officeCode"
            render={({ field: { onChange, onBlur, value } }) => (
              <SelectSearch
                options={officesOptions}
                onChange={(option) => {
                  onChange(option)
                  publishOfficeCode(option?.value || null)
                  handleUpdateOfficeCode(option?.value || null)
                }}
                onBlur={onBlur}
                value={value}
                inputStyle={[
                  styles.select,
                  !allowChangeOffice && { backgroundColor: '#f3f4f6' },
                  !officeCode && {
                    borderColor: '#ED1B2E'
                  }
                ]}
                disableUnderline
                hideLabel
                disabled={!allowChangeOffice}
              />
            )}
          />
          <button
            style={allowChangeOffice ? styles.switchButton : styles.disabledButton}
            type="button"
            onClick={() => handleUpdateOfficeCode(officeCode)}
            disabled={!allowChangeOffice}
          >
            Chuyển
          </button>
        </View>
        <View style={styles.infoText}>
          <Text>Chi nhánh phục vụ: {officeBranchCode || '-'}</Text>
        </View>
        <View style={styles.infoText}>
          <Text>Văn phòng thu phí: {officeBankCode || '-'}</Text>
        </View>
        <View style={styles.infoText}>
          <Text>Ngày GD: {formatDate(today)}</Text>
        </View>
        <View style={styles.infoText}>
          <Text>Ngày LA: {systemDate || '-'}</Text>
        </View>
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: '#e5e7eb',
    paddingTop: 16,
    paddingRight: 40,
    paddingBottom: 16,
    paddingLeft: 40,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12
  },
  title: {
    color: '#374151',
    fontSize: 14,
    fontWeight: 500
  },
  detailsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 16,
    rowGap: 8
  },
  officeGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  label: {
    color: '#374151',
    fontSize: 14
  },
  select: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingRight: 8,
    paddingLeft: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
    width: 110
  },
  switchButton: {
    borderWidth: 0,
    backgroundColor: '#ED1B2E',
    color: '#ffffff',
    borderRadius: 6,
    paddingTop: 9,
    paddingRight: 12,
    paddingBottom: 9,
    paddingLeft: 12,
    fontSize: 13,
    fontWeight: 600
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 8,
    paddingRight: 12,
    paddingBottom: 8,
    paddingLeft: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 6,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7,
    fontSize: 13
  },
  infoText: {
    color: '#374151',
    fontSize: 14
  }
}
